import {Component, Injectable, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import startCase = require('lodash/startCase');
import {FileService} from '../../components/files/service/file.service';
import {DocumentsService} from './model/documents-service';
import {Document, DocumentsModel, Section, DownloadableFile, DocumentType} from './model/documents.model';
import {FileLocationFactory} from '../../components/files/service/file-location.class';

@Component({
    template: require('./documents-page.component.html'),
    styles: [require('../shared/section/section.component.scss'), require('./documents-page.component.scss')]
})
@Injectable()
export class DocumentsPageComponent implements OnDestroy, OnInit {
    private beUrl = `${process.env.BACKEND_BASE_URI}`;
    public documents: DocumentsModel | undefined;
    public downloadPayload: any[];
    public downloadRequestSent: boolean = false;
    public downloads: Array<DownloadableFile>;
    public downloadsError: Error|undefined;
    public downloadsTabId: string = 'downloads-tab';
    public error: Error|undefined;
    public fileDownloadingStatus: { [fileId: string]: boolean } = {};
    public gettingDownloads: boolean = false;
    public indeterminatedGlobal: boolean = false;
    public loading: boolean = false;
    public requestDownloadFailed: boolean = false;
    public requestingDownload: boolean = false;
    public selectedAll: boolean = false;
    public subscriptions: Subscription;

    constructor(private readonly _documentsService: DocumentsService,
                private readonly _fileService: FileService,
                readonly fileLocationFactory: FileLocationFactory) {
        this.subscriptions = new Subscription();
        this.loading = true;
    }

    ngOnInit() {
        this.subscriptions.add(this.getDocuments());
    }

    ngOnDestroy() {
        if (this.subscriptions && !this.subscriptions.closed) {
            this.subscriptions.unsubscribe();
        }
    }

    private transformDetails(data): string {
        const ignoredKeys = ['id', 'type', 'url'];
        let details = '';
        for (let key in data) {
            if (data.hasOwnProperty(key) && ignoredKeys.indexOf(key) === -1) {
                details = `${details}<strong>${startCase(key)}</strong>: ${typeof data[key] === 'boolean' ? (data[key] ? 'Yes' : 'No') : data[key]}<br>`;
            }
        }
        return details;
    }

    private prepareReadableDetails(sections) {
        if (sections && sections.length) {
            sections.forEach(section => {
                if (section.documents && section.documents.length) {
                    section.documents.forEach(doc => {
                        doc.readableDetails = this.transformDetails(doc);
                    });
                }
                if (section.sections && section.sections.length) {
                    this.prepareReadableDetails(section.sections);
                }
            });
        }
    }

    private getDocuments(): Subscription {
        return this._documentsService.getDocuments().subscribe(
            data => {
                if (data && data.sections) {
                    this.prepareReadableDetails(data.sections);
                }
                this.documents = data;
                this.error = undefined;
                this.loading = false;
            },
            err => {
                this.error = err;
                this.loading = false;
            }
        );
    }

    private getDownloadableFiles(): Subscription {
        this.downloads = [];
        this.gettingDownloads = true;
        return this._documentsService.getDownloadableFiles().subscribe(
            list => {
                this.downloads = list.results;
                this.downloadsError = undefined;
                this.gettingDownloads = false;
            },
            err => {
                this.downloadsError = err;
                this.gettingDownloads = false;
            }
        );
    }

    public downloadFile(fileUrl: string, fileId: string) {
        if (!this.fileDownloadingStatus[fileId]) {
            this.fileDownloadingStatus[fileId] = true;
            const fileLocation = this.fileLocationFactory.create('', undefined, undefined, `${this.beUrl}${fileUrl}`);
            this.subscriptions.add(this._fileService.download(fileLocation)
                .finally(() => this.fileDownloadingStatus[fileId] = false)
                .subscribe()
            );
        }
    }

    public getReadableType(typeCode: string) {
        return typeCode ? DocumentType[typeCode] : '';
    }

    public expandSubsection(e) {
        e.stopPropagation();
        e.preventDefault();
        const elem = e.currentTarget.closest('li');
        const subList = elem.querySelector('ul');
        subList.style.display = subList.style.display === 'block' ? 'none' : 'block';
    }

    private changeChildrenSelection(checked: boolean, sections: Section[] | undefined, documents: Document[] | undefined): void {
        if (documents) {
            documents.forEach(document => {
                document.checked = checked;
            });
        }
        if (sections) {
            sections.forEach(aSection => {
                const { documents: subDocuments, sections: subSections } = aSection;
                aSection.checked = checked;
                aSection.indeterminate = false;
                if (subSections && subSections.length || subDocuments && subDocuments.length) {
                    this.changeChildrenSelection(checked, aSection.sections, aSection.documents);
                }
            });
        }
    }

    private changeGlobalSelectionBasedOnChildren(): void {
        if (this.documents) {
            const { sections = [] } = this.documents;
            const sectionsCount = sections.length;
            const indeterminateSectionsCount = sections.filter(section => section.indeterminate).length;
            const checkedSectionsCount = sections.filter(section => section.checked && !section.indeterminate).length;
            this.indeterminatedGlobal = (checkedSectionsCount > 0 && checkedSectionsCount < sectionsCount) || (indeterminateSectionsCount > 0 && indeterminateSectionsCount < sectionsCount);
            this.selectedAll = checkedSectionsCount === sectionsCount;
        }
    }

    private changeParentBasedOnChildren(parentSection: Section): void {
        const { documents = [], sections = [] } = parentSection;
        const childrenCount = documents.length + sections.length;
        const indeterminateChildrenCount = sections.filter(section => section.indeterminate).length;
        let checkedChildrenCount = 0;
        checkedChildrenCount += documents.filter(document => document.checked).length;
        checkedChildrenCount += sections.filter(section => section.checked && !section.indeterminate).length;
        parentSection.indeterminate = (checkedChildrenCount > 0 && checkedChildrenCount < childrenCount) || (indeterminateChildrenCount > 0 && indeterminateChildrenCount < childrenCount);
        parentSection.checked = checkedChildrenCount + indeterminateChildrenCount === childrenCount;
    }

    public onChangeSection(checked: boolean, section: Section, parents: Section[] = []): void {
        section.indeterminate = false;
        this.changeChildrenSelection(checked, section.sections, section.documents);
        parents.forEach(parent => {
            this.changeParentBasedOnChildren(parent);
        });
        this.changeGlobalSelectionBasedOnChildren();
    }

    public onDocChange(parents: Section[]): void {
        parents.forEach(parent => {
            this.changeParentBasedOnChildren(parent);
        });
        this.changeGlobalSelectionBasedOnChildren();
    }

    public onSelectAll(checked: boolean): void {
        if (this.documents) {
            this.changeChildrenSelection(checked, this.documents.sections, undefined);
        }
    }

    private generateDownloadPayload (sections: Section[], parentSectionName?: string) {
        sections.forEach(section => {
            const sectionName = parentSectionName ? `${parentSectionName}/${section.name}` : section.name;
            if (section.documents) {
                section.documents.forEach(doc => {
                    if (doc.checked) {
                        this.downloadPayload.push({
                            id: doc.id,
                            type: doc.type,
                            section: parentSectionName ? `${parentSectionName}/${section.name}` : section.name,
                        });
                    }
                });
            }
            if (section.sections) {
                this.generateDownloadPayload(section.sections, sectionName);
            }
        });
    }

    private requestDownload() {
        this.downloadRequestSent = false;
        this.requestingDownload = true;
        this.requestDownloadFailed = false;
        this._documentsService.requestDownload(this.downloadPayload).subscribe(
            // Success
            () => {
                this.downloadRequestSent = true;
                this.requestingDownload = false;
                this.onSelectAll(false);
                setTimeout(() => {
                    this.downloadRequestSent = false;
                }, 5000);
            },
            // Error
            () => {
                this.downloadRequestSent = false;
                this.requestDownloadFailed = true;
                this.requestingDownload = false;
            }
        );
    }

    public prepareDownload(): void {
        this.downloadPayload = [];
        if (this.documents) {
            this.generateDownloadPayload(this.documents.sections);
            this.requestDownload();
        }
    }

    public downloadPreparedZipFile(e: Event, fileId: string) {
        e.preventDefault();
        if (!this.fileDownloadingStatus[fileId]) {
            this.fileDownloadingStatus[fileId] = true;
            const fileLocation = this.fileLocationFactory.create('', undefined, undefined, `${this.beUrl}/api/document/zip/currentUser?projection=file&id=${fileId}`);
            this.subscriptions.add(this._fileService.download(fileLocation)
                .finally(() => this.fileDownloadingStatus[fileId] = false)
                .subscribe()
            );
        }
    }

    public onTabChange(tabsData) {
        if (tabsData.nextId === this.downloadsTabId) {
            this.subscriptions.add(this.getDownloadableFiles());
        }
    }

    formatDate(date): any {
        if (date) {
            date = new Date(date);
            const options = {year: 'numeric', month: '2-digit', day: '2-digit'};
            return date.toLocaleDateString ? date.toLocaleDateString('en-US', options) : date;
        } else return null;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/documents/documents-page.component.ts